import type { IconType } from 'react-icons';
import {
  HiOutlineRocketLaunch,
  HiOutlineHome,
  HiOutlineChartBarSquare,
  HiOutlineViewColumns,
  HiOutlineCog6Tooth,
  HiOutlineShieldCheck,
  HiOutlineAcademicCap,
} from 'react-icons/hi2';

export interface GuideArticle {
  id: string;
  titleKey: string;
  bodyKey: string;
  tags: string[];
}

export interface GuideSection {
  id: string;
  titleKey: string;
  icon: IconType;
  articles: GuideArticle[];
  adminOnly?: boolean;
}

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'getting-started',
    titleKey: 'guide:sections.gettingStarted.title',
    icon: HiOutlineRocketLaunch,
    articles: [
      { id: 'welcome', titleKey: 'guide:articles.welcome.title', bodyKey: 'guide:articles.welcome.body', tags: ['intro', 'overview', 'about'] },
      { id: 'first-project', titleKey: 'guide:articles.firstProject.title', bodyKey: 'guide:articles.firstProject.body', tags: ['project', 'create', 'new', 'start'] },
      { id: 'n1-vs-n2', titleKey: 'guide:articles.n1VsN2.title', bodyKey: 'guide:articles.n1VsN2.body', tags: ['niveau', 'level', 'estimation', 'detail'] },
      { id: 'navigation', titleKey: 'guide:articles.navigation.title', bodyKey: 'guide:articles.navigation.body', tags: ['sidebar', 'menu', 'navigate', 'tabs'] },
      { id: 'roles', titleKey: 'guide:articles.roles.title', bodyKey: 'guide:articles.roles.body', tags: ['role', 'permission', 'viewer', 'analyst', 'admin', 'access'] },
    ],
  },
  {
    id: 'core',
    titleKey: 'guide:sections.core.title',
    icon: HiOutlineHome,
    articles: [
      { id: 'dashboard', titleKey: 'guide:articles.dashboard.title', bodyKey: 'guide:articles.dashboard.body', tags: ['dashboard', 'home', 'overview', 'summary'] },
      { id: 'projects', titleKey: 'guide:articles.projects.title', bodyKey: 'guide:articles.projects.body', tags: ['project', 'list', 'create', 'manage'] },
      { id: 'project-detail', titleKey: 'guide:articles.projectDetail.title', bodyKey: 'guide:articles.projectDetail.body', tags: ['project', 'tabs', 'estimation', 'demographics', 'subsidies', 'planning', 'finances'] },
      { id: 'reference-projects', titleKey: 'guide:articles.referenceProjects.title', bodyKey: 'guide:articles.referenceProjects.body', tags: ['reference', 'benchmark', 'compare', 'library'] },
    ],
  },
  {
    id: 'analysis',
    titleKey: 'guide:sections.analysis.title',
    icon: HiOutlineChartBarSquare,
    articles: [
      { id: 'analytics', titleKey: 'guide:articles.analytics.title', bodyKey: 'guide:articles.analytics.body', tags: ['analytics', 'charts', 'statistics', 'organization'] },
      { id: 'map', titleKey: 'guide:articles.map.title', bodyKey: 'guide:articles.map.body', tags: ['map', 'geographic', 'location', 'spatial'] },
      { id: 'compare', titleKey: 'guide:articles.compare.title', bodyKey: 'guide:articles.compare.body', tags: ['compare', 'side-by-side', 'projects'] },
      { id: 'simulation', titleKey: 'guide:articles.simulation.title', bodyKey: 'guide:articles.simulation.body', tags: ['simulation', 'scenario', 'what-if', 'model'] },
      { id: 'benchmark', titleKey: 'guide:articles.benchmark.title', bodyKey: 'guide:articles.benchmark.body', tags: ['benchmark', 'performance', 'standard', 'compare'] },
      { id: 'stress-test', titleKey: 'guide:articles.stressTest.title', bodyKey: 'guide:articles.stressTest.body', tags: ['stress', 'test', 'risk', 'sensitivity'] },
      { id: 'ratios', titleKey: 'guide:articles.ratios.title', bodyKey: 'guide:articles.ratios.body', tags: ['ratio', 'indicator', 'financial', 'metric'] },
    ],
  },
  {
    id: 'management',
    titleKey: 'guide:sections.management.title',
    icon: HiOutlineViewColumns,
    articles: [
      { id: 'workflow', titleKey: 'guide:articles.workflow.title', bodyKey: 'guide:articles.workflow.body', tags: ['workflow', 'kanban', 'status', 'board'] },
      { id: 'actuals', titleKey: 'guide:articles.actuals.title', bodyKey: 'guide:articles.actuals.body', tags: ['actuals', 'real', 'data', 'tracking'] },
      { id: 'consultation', titleKey: 'guide:articles.consultation.title', bodyKey: 'guide:articles.consultation.body', tags: ['consultation', 'feedback', 'review', 'comment'] },
      { id: 'optimizer', titleKey: 'guide:articles.optimizer.title', bodyKey: 'guide:articles.optimizer.body', tags: ['optimizer', 'optimize', 'improve', 'suggestion'] },
    ],
  },
  {
    id: 'tools',
    titleKey: 'guide:sections.tools.title',
    icon: HiOutlineCog6Tooth,
    articles: [
      { id: 'activity-log', titleKey: 'guide:articles.activityLog.title', bodyKey: 'guide:articles.activityLog.body', tags: ['activity', 'log', 'history', 'audit'] },
      { id: 'settings', titleKey: 'guide:articles.settings.title', bodyKey: 'guide:articles.settings.body', tags: ['settings', 'profile', 'preferences', 'account'] },
    ],
  },
  {
    id: 'admin',
    titleKey: 'guide:sections.admin.title',
    icon: HiOutlineShieldCheck,
    adminOnly: true,
    articles: [
      { id: 'import', titleKey: 'guide:articles.import.title', bodyKey: 'guide:articles.import.body', tags: ['import', 'upload', 'data', 'csv', 'excel'] },
      { id: 'admin-panel', titleKey: 'guide:articles.adminPanel.title', bodyKey: 'guide:articles.adminPanel.body', tags: ['admin', 'organization', 'users', 'invitations', 'manage'] },
    ],
  },
  {
    id: 'advanced',
    titleKey: 'guide:sections.advanced.title',
    icon: HiOutlineAcademicCap,
    articles: [
      { id: 'versioning', titleKey: 'guide:articles.versioning.title', bodyKey: 'guide:articles.versioning.body', tags: ['version', 'snapshot', 'history', 'rollback'] },
      { id: 'collaboration', titleKey: 'guide:articles.collaboration.title', bodyKey: 'guide:articles.collaboration.body', tags: ['collaboration', 'team', 'share', 'invite'] },
      { id: 'exports', titleKey: 'guide:articles.exports.title', bodyKey: 'guide:articles.exports.body', tags: ['export', 'pdf', 'excel', 'download', 'report'] },
      { id: 'ai-assistant', titleKey: 'guide:articles.aiAssistant.title', bodyKey: 'guide:articles.aiAssistant.body', tags: ['ai', 'assistant', 'chat', 'help', 'copilot'] },
    ],
  },
];

const ROUTE_TO_SECTION: Record<string, string> = {
  '/': 'core',
  '/projects': 'core',
  '/reference-projects': 'core',
  '/analytics': 'analysis',
  '/map': 'analysis',
  '/compare': 'analysis',
  '/simulation': 'analysis',
  '/benchmark': 'analysis',
  '/stress-test': 'analysis',
  '/ratios': 'analysis',
  '/workflow': 'management',
  '/actuals': 'management',
  '/consultation': 'management',
  '/optimizer': 'management',
  '/activity': 'tools',
  '/settings': 'tools',
  '/import': 'admin',
  '/admin': 'admin',
};

export function getGuideSectionForRoute(pathname: string): string | null {
  // Exact match first
  if (ROUTE_TO_SECTION[pathname]) return ROUTE_TO_SECTION[pathname];

  // Prefix matches
  if (pathname.startsWith('/projects/')) return 'core';
  if (pathname.startsWith('/analytics/')) return 'analysis';
  if (pathname.startsWith('/reference-projects/')) return 'core';

  return null;
}

/** Returns an i18n key (nav namespace) for the current page, to be resolved with t(). */
export function getPageNameKeyForRoute(pathname: string): string | null {
  if (pathname === '/') return 'nav:dashboard';
  if (pathname === '/projects') return 'nav:projects';
  if (pathname.startsWith('/projects/')) return 'guide:articles.projectDetail.title';
  if (pathname.startsWith('/reference-projects')) return 'nav:reference';
  if (pathname.startsWith('/analytics')) return 'nav:analytics';
  if (pathname === '/map') return 'nav:map';
  if (pathname === '/compare') return 'nav:compare';
  if (pathname === '/simulation') return 'nav:simulation';
  if (pathname === '/benchmark') return 'nav:benchmark';
  if (pathname === '/stress-test') return 'nav:stress-test';
  if (pathname === '/ratios') return 'nav:ratios';
  if (pathname === '/workflow') return 'nav:workflow';
  if (pathname === '/actuals') return 'nav:actuals';
  if (pathname === '/consultation') return 'nav:consultation';
  if (pathname === '/optimizer') return 'nav:optimizer';
  if (pathname === '/activity') return 'nav:activity';
  if (pathname === '/settings') return 'nav:settings';
  if (pathname === '/import') return 'nav:import';
  if (pathname === '/admin') return 'nav:admin';
  return null;
}
